<template>
  <div class="min-h-screen bg-gray-100">    
    <div class="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <!-- Container que cambia según el tamaño de pantalla -->
      <div class="w-full max-w-md p-6 relative">
        
        <!-- Pantalla principal de login -->
        <div v-if="!showSupport">
          <!-- Logo Horizontal de la institución (centrado y con un 50% de ancho) -->
          <div class="flex justify-start pl-11 mb-10">
            <img 
              src="/images/logo-portal.png"
              alt="Logo Portal Estudiantil" 
              class="w-4/5" />
          </div>
          <!-- Botón de Google -->
          <div>
            <button 
              @click="loginWithGoogle" 
              :disabled="isLoading"
              class="w-full px-4 py-2 bg-blue-500 text-white rounded-lg shadow hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              <span v-if="isLoading">Iniciando sesión...</span>
              <span v-else>Acceder con correo institucional</span>
            </button>
          </div>
          
          <!-- Mensaje de error -->
          <div 
            v-if="errorMessage" 
            class="mt-4 bg-red-100 border border-red-400 text-center text-red-700 px-4 py-3 rounded-lg mb-4"
          >
            {{ errorMessage }}
          </div>
          
          <!-- Mensaje de éxito (para logout) -->
          <div 
            v-if="successMessage" 
            class="mt-4 bg-green-100 border border-green-400 text-center text-green-700 px-4 py-3 rounded-lg mb-4"
          >
            {{ successMessage }}
          </div>
        </div>

        <!-- Pantalla de soporte -->
        <div v-else class="space-y-4">
          <!-- Título -->
          <h2 class="text-xl font-semibold text-gray-800 mb-4 text-center">
            Soporte Técnico del Portal Estudiantil
          </h2>
          <!-- Mensaje de ayuda -->
          <div class="bg-white rounded-lg shadow-md p-6 space-y-4 text-gray-700">
            <p class="text-sm leading-relaxed">
              <strong>Estimado(a) alumno(a):</strong>
            </p>
            <p class="text-sm leading-relaxed">
              Si presenta problemas para ingresar al Portal Estudiantil, favor de solicitar apoyo enviando un correo electrónico a 
                  <a 
                    :href="mailtoLink"
                    class="text-blue-600 hover:text-blue-800 underline text-sm"
                  >
                    soporte.alumnos@atlantico53.com
                  </a> con el asunto "Soporte Portal", redactar el correo de la siguiente forma:
            </p>

            <!-- Información de contacto -->
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
              <div class="flex items-start space-x-2">
                <span class="text-lg">📧</span>
                <div class="flex-1">
                  <p class="text-sm font-semibold text-gray-800">Correo:</p>
                  <a 
                    :href="mailtoLink"
                    class="text-blue-600 hover:text-blue-800 underline text-sm break-all"
                  >
                    soporte.alumnos@atlantico53.com
                  </a>
                </div>
              </div>
              
              <div class="flex items-start space-x-2">
                <span class="text-lg">📝</span>
                <div class="flex-1">
                  <p class="text-sm font-semibold text-gray-800">Asunto:</p>
                  <p class="text-sm text-gray-700">Soporte Portal</p>
                </div>
              </div>
            </div>

            <!-- Información a incluir -->
            <div class="space-y-2">
              <p class="text-sm font-semibold text-gray-800">
                Incluyendo la siguiente información:
              </p>
              <ul class="space-y-1 text-sm text-gray-700 ml-4">
                <li class="flex items-start">
                  <span class="mr-2">🆔</span>
                  <span>Matrícula:</span>
                </li>
                <li class="flex items-start">
                  <span class="mr-2">👤</span>
                  <span>Nombre completo:</span>
                </li>
                <li class="flex items-start">
                  <span class="mr-2">🏫</span>
                  <span>Campus:</span>
                </li>
                <li class="flex items-start">
                  <span class="mr-2">⏰</span>
                  <span>Turno:</span>
                </li>
                <li class="flex items-start">
                  <span class="mr-2">👥</span>
                  <span>Grupo:</span>
                </li>
              </ul>
            </div>

            <!-- Teléfono -->
            <div class="bg-green-50 border border-green-200 rounded-lg p-4">
              <div class="flex items-start space-x-2">
                <span class="text-lg">📞</span>
                <div class="flex-1">
                  <p class="text-sm text-gray-700">
                    Asimismo, puede comunicarse al número telefónico 
                    <a href="tel:8993200010" class="font-semibold text-green-700 hover:text-green-900">
                      899 320 0010
                    </a> 
                    para recibir asistencia.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Botón de regresar -->
          <button 
            @click="showSupport = false"
            class="w-full px-4 py-2 bg-gray-500 text-white rounded-lg shadow hover:bg-gray-600 transition-all duration-300 flex items-center justify-center space-x-2"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            <span>Regresar</span>
          </button>
        </div>
        
        <!-- Botón flotante de ayuda-->
        <button 
          v-if="!showSupport"
          @click="showSupport = true"
          class="fixed bottom-6 right-6 md:bottom-8 md:right-8 w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg hover:shadow-xl hover:bg-blue-700 transition-all duration-300 flex items-center justify-center z-50 group"
          aria-label="Ayuda y soporte"
        >
          <!-- Icono de ayuda -->
          <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          
          <!-- Efecto de pulso -->
          <span class="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping"></span>
          
          <!-- Tooltip -->
          <span class="absolute bottom-full right-0 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            ¿Necesitas ayuda?
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      errorMessage: '',
      successMessage: '',
      isLoading: false,
      showSupport: false,
      googleConfig: {
        client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
        auto_select: false,
        cancel_on_tap_outside: true,
        prompt_parent_id: 'prompt_container',
      }
    };
  },
  computed: {
    mailtoLink() {
      const email = 'soporte.alumnos@atlantico53.com';
      const subject = 'Soporte Portal';
      const body = `

- 🆔 Matrícula:
- 👤 Nombre completo:
- 🏫 Campus:
- ⏰ Turno:
- 👥 Grupo:`;
      
      return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  },
  mounted() {
    // Capturar parámetros de la URL
    const urlParams = new URLSearchParams(window.location.search);
    
    // Verificar si hay un error en la URL
    const errorParam = urlParams.get('error');
    if (errorParam) {
      this.errorMessage = decodeURIComponent(errorParam);
      // Limpiar la URL sin recargar la página
      this.clearUrlParams();
    }
    
    // Verificar si estamos regresando de un cierre de sesión
    const isLoggedOut = urlParams.get('logout');
    if (isLoggedOut) {
      // Limpia cualquier estado local de autenticación de Google
      this.successMessage = 'Has cerrado sesión correctamente.';
      this.clearGoogleSession();
      // Limpiar la URL sin recargar la página
      this.clearUrlParams();
    }
  },
  methods: {
    loginWithGoogle() {
      this.isLoading = true;
      this.clearError();
      this.clearSuccess();
      
      // Redirigir a la ruta del backend para iniciar sesión con Google
      window.location.href = process.env.VUE_APP_API_INTERNAL + '/auth/google';
    },
    
    clearError() {
      this.errorMessage = '';
    },
    
    clearSuccess() {
      this.successMessage = '';
    },
    
    clearUrlParams() {
      // Limpiar parámetros de la URL sin recargar la página
      const url = new URL(window.location);
      url.searchParams.delete('error');
      url.searchParams.delete('logout');
      window.history.replaceState({}, document.title, url.pathname);
    },
    
    clearGoogleSession() {
      // Borrar cualquier estado local relacionado con Google
      localStorage.removeItem('googleToken');
      
      // También puede usar google-one-tap para limpiar estado
      if (window.google && window.google.accounts) {
        window.google.accounts.id.disableAutoSelect();
      }
      
      // Esta función simulará un cierre completo de sesión
      if (document.cookie) {
        const cookies = document.cookie.split(';');
        
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
          document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
        }
      }
    }
  }
};
</script>